import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { useAuth } from '../stores/auth';
import { Eye, EyeOff, Loader2, ArrowLeft, CheckCircle, Shield } from 'lucide-react';

type PasswordStrength = 'weak' | 'medium' | 'strong';

function getPasswordStrength(password: string): PasswordStrength {
  if (password.length === 0) return 'weak';

  const hasMinLength = password.length >= 8;
  const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasUppercase = /[A-Z]/.test(password);

  let score = 0;
  if (password.length >= 6) score++;
  if (hasMinLength) score++;
  if (hasSpecialChars) score++;
  if (hasNumbers) score++;
  if (hasUppercase) score++;

  if (score >= 4) return 'strong';
  if (score >= 2) return 'medium';
  return 'weak';
}

const strengthConfig: Record<PasswordStrength, { label: string; color: string; barColor: string; width: string }> = {
  weak: { label: 'Fraca', color: 'text-red-400', barColor: 'bg-red-400', width: 'w-1/3' },
  medium: { label: 'Média', color: 'text-yellow-400', barColor: 'bg-yellow-400', width: 'w-2/3' },
  strong: { label: 'Forte', color: 'text-green-400', barColor: 'bg-green-400', width: 'w-full' },
};

export default function FirstAccess() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const [email, setEmail] = useState(user?.email || '');
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showTempPassword, setShowTempPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const passwordStrength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/first-access', {
        email,
        tempPassword,
        newPassword,
      });
      setSuccess('Senha alterada com sucesso! Redirecionando para o login...');
      setTimeout(() => {
        setLocation('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erro ao alterar senha. Verifique os dados informados.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center p-4 overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
        {/* Animated orbs */}
        <div
          className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-float"
          style={{ background: 'hsl(var(--accent-cyan) / 0.3)' }}
        />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-float"
          style={{ background: 'hsl(var(--accent-violet) / 0.3)', animationDelay: '1s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl animate-pulse"
          style={{ background: 'hsl(var(--primary) / 0.2)' }}
        />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div
        className={`relative w-full max-w-sm transition-all duration-700 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Header */}
        <div className="mb-8 text-center">
          <div
            className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl text-white shadow-2xl animate-float"
            style={{
              animationDuration: '4s',
              background: 'linear-gradient(135deg, hsl(217 91% 60%), hsl(224 76% 48%))',
              boxShadow: '0 0 30px hsl(var(--primary) / 0.4)',
            }}
          >
            <Shield className="h-10 w-10 drop-shadow-lg" />
          </div>
          <h1 className="text-2xl font-bold text-white drop-shadow-lg">Primeiro Acesso</h1>
          <p className="text-blue-200/80 font-medium mt-1">Defina sua nova senha para continuar</p>
        </div>

        {/* Form Card */}
        <form
          onSubmit={handleSubmit}
          className="space-y-4 p-8 rounded-3xl shadow-2xl border border-white/20"
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          {/* Success Banner */}
          {success && (
            <div className="flex items-center gap-3 rounded-2xl p-4 text-sm text-white border border-green-500/30 animate-fade-in"
                 style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* Error Banner */}
          {error && (
            <div className="rounded-2xl p-4 text-sm text-white animate-shake border border-red-500/30"
                 style={{ background: 'rgba(239, 68, 68, 0.2)' }}>
              {error}
            </div>
          )}

          {/* Email */}
          <div className="space-y-2">
            <label htmlFor="fa-email" className="text-sm font-semibold text-white/90">
              E-mail
            </label>
            <input
              id="fa-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              required
              disabled={isLoading || !!success}
              className="w-full rounded-2xl border-2 border-white/20 px-4 py-4 text-white placeholder:text-white/50
                focus:border-white/40 focus:outline-none focus:ring-4 focus:ring-white/10
                disabled:opacity-50 transition-all duration-300"
              style={{ background: 'rgba(255, 255, 255, 0.1)' }}
            />
          </div>

          {/* Temp Password */}
          <div className="space-y-2">
            <label htmlFor="fa-temp-password" className="text-sm font-semibold text-white/90">
              Senha Temporária
            </label>
            <div className="relative">
              <input
                id="fa-temp-password"
                type={showTempPassword ? 'text' : 'password'}
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="Senha recebida por e-mail"
                required
                disabled={isLoading || !!success}
                className="w-full rounded-2xl border-2 border-white/20 px-4 py-4 text-white placeholder:text-white/50
                  focus:border-white/40 focus:outline-none focus:ring-4 focus:ring-white/10
                  disabled:opacity-50 transition-all duration-300 pr-12"
                style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              />
              <button
                type="button"
                onClick={() => setShowTempPassword(!showTempPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-1"
              >
                {showTempPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <label htmlFor="fa-new-password" className="text-sm font-semibold text-white/90">
              Nova Senha
            </label>
            <div className="relative">
              <input
                id="fa-new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                disabled={isLoading || !!success}
                className="w-full rounded-2xl border-2 border-white/20 px-4 py-4 text-white placeholder:text-white/50
                  focus:border-white/40 focus:outline-none focus:ring-4 focus:ring-white/10
                  disabled:opacity-50 transition-all duration-300 pr-12"
                style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-1"
              >
                {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {newPassword.length > 0 && (
              <div className="space-y-1.5 animate-fade-in">
                <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${strengthConfig[passwordStrength].barColor} ${strengthConfig[passwordStrength].width}`}
                  />
                </div>
                <p className={`text-xs font-medium ${strengthConfig[passwordStrength].color}`}>
                  Senha: {strengthConfig[passwordStrength].label}
                </p>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <label htmlFor="fa-confirm-password" className="text-sm font-semibold text-white/90">
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <input
                id="fa-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a nova senha"
                required
                disabled={isLoading || !!success}
                className="w-full rounded-2xl border-2 border-white/20 px-4 py-4 text-white placeholder:text-white/50
                  focus:border-white/40 focus:outline-none focus:ring-4 focus:ring-white/10
                  disabled:opacity-50 transition-all duration-300 pr-12"
                style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-1"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {/* Mismatch warning */}
            {confirmPassword.length > 0 && newPassword !== confirmPassword && (
              <p className="text-xs text-red-400 animate-fade-in">As senhas não coincidem</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !!success}
            className="relative w-full overflow-hidden rounded-2xl py-4 font-bold text-blue-700
              disabled:opacity-50
              flex items-center justify-center gap-2 transition-all duration-300
              active:scale-[0.98] group"
            style={{
              background: 'linear-gradient(135deg, #ffffff, #e0e7ff)',
              boxShadow: '0 10px 30px rgba(255, 255, 255, 0.2)',
            }}
          >
            {/* Shimmer effect */}
            <div
              className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              }}
            />

            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="relative">Alterando senha...</span>
              </>
            ) : (
              <span className="relative">Alterar Senha</span>
            )}
          </button>
        </form>

        {/* Back to Login */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setLocation('/login')}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para o login
          </button>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-white/50">v1.0.0 - Prisma by NossoClube Apps</p>
      </div>
    </div>
  );
}
